import React, { useState, useCallback } from "react";
import axios from '../../api/apiClient';
import {
  Button,
  GeneralMessage,
  useGeneralMessage
} from '../../common/APICommon';
import { SetAuditTrailBulk } from './../Reports/AuditTrailReportEmployeeSetup';
import { token, domain } from '../Security';
import { LoadingSpinner } from '../../common/Loading';

// Icons
import UploadIcon from '@mui/icons-material/Upload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DevicesIcon from '@mui/icons-material/Devices';
import FilePresentIcon from '@mui/icons-material/FilePresent';

import ExcelJS from "exceljs";

function ExcelUploadHelper({ user, isHideSideMenu, refreshEmployeeData }) {
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();
  const [loadingRequest, setLoadingRequest] = useState(false);

  // --- Modal State ---
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [activeMode, setActiveMode] = useState(null); // 'DEVICE' or 'RESTDAY'

  // --- File Processing State ---
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [previewData, setPreviewData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(null);

  // --- CONFIGURATION OBJECTS ---
  const MODES = {
    DEVICE: {
      key: 'DEVICE',
      title: "Upload Device Numbers",
      templateName: "device_number_template.csv",
      templateData: [
        { EmployeeId: "123123123", Col2: "2862" },
        { EmployeeId: "213123123", Col2: "2654" }
      ],
      headers: ["EmployeeId", "DeviceNumber"],
      description: [
        "Fill in EmployeeId and DeviceNumber columns.",
        "DeviceNumber should contain only numeric values."
      ],
      apiUrl: "/api/Employee/helper/upload-bulk-devicenumber",
      auditField: "Device Number Bulk Update"
    },
    RESTDAY: {
      key: 'RESTDAY',
      title: "Upload Rest Days",
      templateName: "rest_day_template.csv",
      templateData: [
        { EmployeeId: "223070501", Col2: "NONE" },
        { EmployeeId: "223100901", Col2: "SUNDAY" }
      ],
      headers: ["EmployeeId", "Restday"],
      description: [
        "Fill in EmployeeId and Restday.",
        "Accepted: SUNDAY, MONDAY, TUESDAY, WEDNESDAY, THURSDAY, FRIDAY, SATURDAY.",
        "Use NONE to remove rest days. Add multiple rows for multiple rest days."
      ],
      apiUrl: "/api/Employee/helper/upload-bulk-restdays",
      auditField: "Rest Days Bulk Update"
    }
  };

  // --- HANDLERS ---

  const handleOpenModal = (mode) => {
    setActiveMode(mode);
    setShowUploadForm(true);
    // Reset
    setFile(null);
    setFileName("");
    setPreviewData([]);
    setUploadStatus(null);
    setValidationErrors([]);
  };

  const handleClose = () => {
    setShowUploadForm(false);
    // Slight delay to clear mode so animation doesn't look weird
    setTimeout(() => setActiveMode(null), 200);
    setFile(null);
    setFileName("");
    setPreviewData([]);
    setUploadStatus(null);
    setValidationErrors([]);
  };

  // CORE FILE PROCESSOR (Used by both Input and Drop)
  const processFile = (selectedFile) => {
    if (!selectedFile) return;

    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      showGeneralMessage("Please upload only CSV or Excel files", false);
      return;
    }

    setFile(selectedFile);
    setFileName(selectedFile.name);
    setValidationErrors([]);
    setUploadStatus(null);
    
    // Trigger preview
    previewFile(selectedFile);
  };

  const previewFile = async (uploadedFile) => {
    try {
      const workbook = new ExcelJS.Workbook();
      const arrayBuffer = await uploadedFile.arrayBuffer();
      await workbook.xlsx.load(arrayBuffer);

      const isValidValue = (value) => {
        if (!value) return false;
        if (typeof value === "string" && value.trim() === "") return false;
        if (typeof value === "string" && /^<.*>$/.test(value)) return false;
        return true;
      };

      const sheet = workbook.getWorksheet("Sheet1") || workbook.worksheets[0];
      const parsedData = [];

      sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber === 1) return; // skip header

        // ExcelJS is 1-based index
        const col1 = row.getCell(1).value;
        const col2 = row.getCell(2).value;

        parsedData.push({
          EmployeeId: isValidValue(col1) ? String(col1).trim() : "",
          Value: isValidValue(col2) ? String(col2).trim() : "",
        });
      });
      
      setPreviewData(parsedData);

    } catch (error) {
      console.error("Error reading Excel file:", error);
      showGeneralMessage("Error reading file. Ensure it is a valid Excel/CSV.", false);
    }
  };

  const validateData = () => {
    const errors = [];
    if (!file) errors.push("Please select a file to upload");
    if (previewData.length === 0) errors.push("No valid data found in the file");

    previewData.forEach((row, index) => {
      // Generic check
      if (!row.EmployeeId || !row.Value) {
        errors.push(`Row ${index + 2}: Missing required data.`);
      }

      // Specific Validation
      if (activeMode === 'DEVICE') {
        if (row.Value && !/^\d+$/.test(row.Value)) {
          errors.push(`Row ${index + 2}: DeviceNumber must be numeric.`);
        }
      } 
      else if (activeMode === 'RESTDAY') {
        const validDays = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "NONE"];
        if (row.Value && !validDays.includes(row.Value.toUpperCase())) {
          errors.push(`Row ${index + 2}: Invalid Restday '${row.Value}'.`);
        }
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateData()) {
      showGeneralMessage("Please fix validation errors before uploading", false);
      return;
    }
    
    try {
      setLoadingRequest(true);
      const config = MODES[activeMode];

      // Prepare Payload
      let payload = [];
      if (activeMode === 'DEVICE') {
        payload = previewData.map(i => ({ employeeId: i.EmployeeId, deviceNumber: i.Value }));
      } else {
        payload = previewData.map(i => ({ EmployeeId: i.EmployeeId, Day: i.Value.toUpperCase() }));
      }

      const apiUrl = domain + config.apiUrl;
      
      console.log("URL : ", apiUrl, payload);

      const response = await axios.post(apiUrl, payload, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      // Audit Trail
      if (user && user.id) {
        await SetAuditTrailBulk({
          data: [{
            setupArea: "Employee Personal Information",
            actionTaken: "update",
            field: config.auditField,
            newValue: `Updated ${response.data.updatedCount || payload.length} records`,
            oldValue: "-",
            dateSaved: new Date(),
            savedById: user.id,
          }]
        });
      }

      if (refreshEmployeeData) refreshEmployeeData();

      setUploadStatus({
        success: true,
        message: response.data.message || `Successfully processed records.`,
        count: response.data.updatedCount || payload.length
      });

      showGeneralMessage("Records processed successfully.", true);

      // Auto close
      setTimeout(() => { handleClose(); }, 2000);

    } catch (error) {
      console.error('Error uploading:', error);
      let msg = error.response?.data?.message || "Failed to upload.";
      setUploadStatus({ success: false, message: msg, error: error.response?.data });
      showGeneralMessage(msg, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const downloadTemplate = () => {
    const config = MODES[activeMode];
    const csvContent = [
      config.headers,
      ...config.templateData.map(row => [row.EmployeeId, row.Col2])
    ].map(row => row.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", config.templateName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileName = (name) => {
    return name.length > 30 ? name.substring(0, 15) + "..." + name.substring(name.length - 15) : name;
  };

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="MainteInfo shadow-xl">
            <div className="mainteHeader">
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Employee >"} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"Batch Uploads"}</span>
              </div>
              <h3>Employee Batch Upload Helpers</h3>
            </div>
            
            <div className='p-6 grid grid-cols-1 md:grid-cols-2 gap-6'>
              
              {/* --- CARD 1: DEVICE NUMBERS --- */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-blue-100 rounded-full text-blue-600 mr-4">
                    <DevicesIcon fontSize="large" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">Device Numbers</h4>
                    <p className="text-sm text-gray-500">Update biometrics IDs in bulk</p>
                  </div>
                </div>
                <div className="bg-blue-50 p-3 rounded mb-4 text-sm text-blue-800">
                  <ul className="list-disc pl-4 space-y-1">
                    <li>Requires EmployeeId and DeviceNumber</li>
                    <li>Numbers only</li>
                  </ul>
                </div>
                <Button
                  icon={<UploadIcon />}
                  name={"Upload Device No."}
                  className={"w-full bg-blue-600 hover:bg-blue-700 border-none cursor-pointer py-2"}
                  onClick={() => handleOpenModal('DEVICE')}
                />
              </div>

              {/* --- CARD 2: REST DAYS --- */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-green-100 rounded-full text-green-600 mr-4">
                    <AccessTimeIcon fontSize="large" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">Rest Days</h4>
                    <p className="text-sm text-gray-500">Update schedule rest days</p>
                  </div>
                </div>
                <div className="bg-green-50 p-3 rounded mb-4 text-sm text-green-800">
                  <ul className="list-disc pl-4 space-y-1">
                    <li>Requires EmployeeId and Day Name</li>
                    <li>Supports multiple days per employee</li>
                  </ul>
                </div>
                <Button
                  icon={<UploadIcon />}
                  name={"Upload Rest Days"}
                  className={"w-full bg-green-600 hover:bg-green-700 border-none cursor-pointer py-2"}
                  onClick={() => handleOpenModal('RESTDAY')}
                />
              </div>

            </div>
          </div>
        </div>
      </div>

      <UploadForm
        isOpen={showUploadForm}
        onClose={handleClose}
        config={activeMode ? MODES[activeMode] : null}
        file={file}
        fileName={fileName}
        previewData={previewData}
        validationErrors={validationErrors}
        uploadStatus={uploadStatus}
        processFile={processFile}
        handleSubmit={handleSubmit}
        formatFileName={formatFileName}
        downloadTemplate={downloadTemplate}
      />

      <GeneralMessage
        form={showMessage}
        message={message}
        isSuccess={isSuccess}
      />

      <LoadingSpinner loading={loadingRequest} />
    </div>
  )
}

// --- SUB COMPONENT: UPLOAD FORM (WITH DRAG & DROP) ---

function UploadForm({
  isOpen,
  onClose,
  config,
  file,
  fileName,
  previewData,
  validationErrors,
  uploadStatus,
  processFile,
  handleSubmit,
  formatFileName,
  downloadTemplate
}) {
  const [isDragging, setIsDragging] = useState(false);

  if (!isOpen || !config) return null;

  // --- Drag & Drop Handlers ---
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // This is required to allow the drop
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  return (
    <div 
      role="dialog" 
      className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto"
    >
      <div className="bg-gray-50 w-full md:w-[90%] lg:w-[70%] xl:w-[60%] rounded-lg shadow-xl h-auto max-h-[90vh] flex flex-col relative">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
            <CloudUploadIcon className="text-blue-500" />
            <span className="text-xl text-gray-600">{config.title}</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1 bg-transparent border-none cursor-pointer">
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-grow space-y-6">

          {/* Instructions Block */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <DescriptionIcon className="text-blue-500 mr-3 mt-1" />
              <div>
                <h4 className="font-semibold text-blue-800 mb-2">Instructions</h4>
                <ul className="list-disc pl-5 text-blue-700 space-y-1 text-sm">
                  <li>Download the template CSV file below.</li>
                  {config.description.map((desc, i) => (
                    <li key={i}>{desc}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* DRAG & DROP AREA */}
          <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
            <div className="py-2 border-b border-gray-300">
              <h3 className="font-medium text-gray-700">Select File</h3>
            </div>
            
            <div className="pb-4 pt-4">
              <div 
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`
                  flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 transition-colors
                  ${isDragging 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                  }
                `}
              >
                <CloudUploadIcon 
                  className={`text-4xl mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} 
                />
                
                <p className="text-gray-600 mb-3 font-medium">
                  {isDragging ? "Drop the file here" : "Drag and drop file here"}
                </p>
                
                <span className="text-gray-400 text-sm mb-4">- OR -</span>

                <label htmlFor="file-upload" className="cursor-pointer">
                  <span className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md cursor-pointer transition-colors inline-flex items-center shadow-sm">
                    <UploadIcon className="mr-2" fontSize="small" />
                    Browse Files
                  </span>
                  <input 
                    id="file-upload" 
                    type="file" 
                    accept=".csv,.xlsx,.xls" 
                    onChange={handleInputChange} 
                    className="hidden" 
                  />
                </label>
                
                <p className="text-gray-400 text-xs mt-3">
                  Supports: .csv, .xlsx, .xls
                </p>

                {/* File Selected Indicator */}
                {fileName && (
                  <div className="mt-4 p-3 bg-white rounded-lg border border-blue-200 w-full flex items-center justify-between shadow-sm animate-fade-in">
                     <div className="flex items-center overflow-hidden">
                        <div className="bg-blue-100 p-2 rounded-full mr-3">
                           <FilePresentIcon className="text-blue-600" />
                        </div>
                        <span className="text-gray-700 font-medium truncate max-w-[200px] md:max-w-[300px]">
                          {formatFileName(fileName)}
                        </span>
                      </div>
                      <span className="text-green-600 text-sm font-semibold flex items-center whitespace-nowrap ml-2">
                        <CheckCircleIcon fontSize="small" className="mr-1"/> Ready
                      </span>
                  </div>
                )}
              </div>

              <div className="mt-4 flex justify-end">
                <button 
                  onClick={downloadTemplate} 
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center bg-transparent border-none cursor-pointer underline"
                >
                  <DownloadIcon fontSize="small" className="mr-1" />
                  Download template CSV
                </button>
              </div>
            </div>
          </div>

          {/* Data Preview */}
          {previewData.length > 0 && (
            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
              <div className="py-2 border-b border-gray-300 bg-gray-50 -mx-4 px-4 rounded-t-lg">
                <h3 className="font-medium text-gray-700">Preview (First 5 rows)</h3>
              </div>
              <div className="py-3 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase">Row</th>
                      <th className="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase">{config.headers[0]}</th>
                      <th className="px-3 py-3 text-left text-xs font-bold text-gray-500 uppercase">{config.headers[1]}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {previewData.slice(0, 5).map((row, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-2 text-sm text-gray-500">{index + 1}</td>
                        <td className="px-3 py-2 text-sm font-medium text-gray-900">{row.EmployeeId}</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{row.Value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewData.length > 5 && (
                  <div className="text-center text-xs text-gray-400 mt-2 italic">
                    ... and {previewData.length - 5} more rows
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="border border-red-200 rounded-lg bg-red-50 px-4 shadow-sm py-3">
              <h3 className="font-medium text-red-700 flex items-center border-b border-red-200 pb-2 mb-2">
                <ErrorIcon className="mr-2" /> Validation Errors
              </h3>
              <ul className="list-disc pl-5 text-red-700 space-y-1">
                {validationErrors.slice(0, 5).map((error, index) => (
                  <li key={index} className="text-sm">{error}</li>
                ))}
                {validationErrors.length > 5 && <li className="text-sm font-semibold">...and {validationErrors.length - 5} more errors</li>}
              </ul>
            </div>
          )}

          {/* Status */}
          {uploadStatus && (
            <div className={`border rounded-lg px-4 shadow-sm py-3 ${uploadStatus.success ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
              <h3 className={`font-medium flex items-center ${uploadStatus.success ? 'text-green-700' : 'text-red-700'}`}>
                {uploadStatus.success ? <CheckCircleIcon className="mr-2" /> : <ErrorIcon className="mr-2" />}
                {uploadStatus.message}
              </h3>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-5 border-t border-gray-200 bg-white rounded-b-lg shadow-sm">
          <button 
            onClick={onClose} 
            className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors"
          >
            Cancel
          </button>
          
          <button 
            onClick={handleSubmit} 
            disabled={!file || validationErrors.length > 0} 
            className={`px-6 py-2 rounded border-none cursor-pointer flex items-center font-medium shadow-sm transition-colors ${
              !file || validationErrors.length > 0 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            <UploadIcon className="mr-2" fontSize="small" /> 
            Upload Data
          </button>
        </div>

      </div>
    </div>
  );
}

export default ExcelUploadHelper;